// Helper функции для работы с данными

/**
 * Конвертирует байты в человекочитаемый формат
 * @param {number} bytes - количество байт
 * @returns {string} - отформатированная строка
 */
export const bytesToHuman = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Конвертирует мегабайты в человекочитаемый формат
 * @param {number} mb - количество мегабайт
 * @returns {string} - отформатированная строка
 */
export const megabytesToHuman = (mb) => {
  return bytesToHuman(mb * 1024 * 1024);
};

/**
 * Конвертирует байты в мегабайты
 * @param {number} bytes - количество байт
 * @returns {number} - количество мегабайт
 */
export const bytesToMegabytes = (bytes) => {
  return bytes / (1024 * 1024);
};

/**
 * Форматирует дату в локальный формат
 * @param {string|Date} date - дата
 * @returns {string} - отформатированная дата
 */
export const formatDate = (date) => {
  return new Date(date).toLocaleString('ru-RU');
};

/**
 * Форматирует время игры в часы и минуты
 * @param {number} minutes - количество минут
 * @returns {string} - отформатированное время
 */
export const formatPlayTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}ч ${mins}м`;
};

/**
 * Проверяет, является ли значение пустым
 * @param {*} value - значение для проверки
 * @returns {boolean} - true если пустое
 */
export const isEmpty = (value) => {
  return value === null || value === undefined || value === '';
};

/**
 * Безопасно получает значение из объекта по пути
 * @param {object} obj - объект
 * @param {string} path - путь к свойству
 * @param {*} defaultValue - значение по умолчанию
 * @returns {*} - значение свойства
 */
export const get = (obj, path, defaultValue = null) => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return defaultValue;
    }
  }
  
  return result;
}; 

/**
 * Конвертирует steamid64 в STEAM_0:X:Z
 * @param {string} steamid64
 * @returns {string}
 */
export function steamId64ToV2(steamid64) {
  if (!steamid64) return '';
  const base = '76561197960265728';
  function subtractStrings(a, b) {
    let result = '';
    let borrow = 0;
    a = String(a).split('').reverse();
    b = String(b).split('').reverse();
    let len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
      let digitA = parseInt(a[i] || '0', 10);
      let digitB = parseInt(b[i] || '0', 10) + borrow;
      if (digitA < digitB) {
        digitA += 10;
        borrow = 1;
      } else {
        borrow = 0;
      }
      result = (digitA - digitB) + result;
    }
    return result.replace(/^0+/, '') || '0';
  }
  const diff = subtractStrings(steamid64, base);
  const num = parseInt(diff, 10);
  const Y = num % 2;
  const Z = Math.floor(num / 2);
  return `STEAM_0:${Y}:${Z}`;
} 

/**
 * Конвертирует STEAM_0:X:Z в steamid64
 * @param {string} steamID
 * @returns {string}
 */
export function normalizeSteamId(steamId) {
  if (!steamId) return '';
  const s = String(steamId).trim();
  if (/^\d{17}$/.test(s)) return s;
  const m = s.match(/^STEAM_[01]:(\d+):(\d+)$/i);
  if (m) return `STEAM_0:${m[1]}:${m[2]}`;
  return s;
}

export function steamIdTo64(steamID) {
  if (!steamID) return '';
  const normalized = normalizeSteamId(steamID);
  if (/^\d{17}$/.test(normalized)) return normalized;
  if (!normalized.startsWith('STEAM_')) return '';
  const parts = normalized.split(':');
  if (parts.length !== 3) return '';
  const Y = parseInt(parts[1], 10);
  const Z = parseInt(parts[2], 10);
  const base = '76561197960265728';
  const offset = (Z * 2 + Y).toString();
  function addStrings(a, b) {
    let res = '';
    let carry = 0;
    a = a.split('').reverse();
    b = b.split('').reverse();
    let len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
      let digitA = parseInt(a[i] || '0', 10);
      let digitB = parseInt(b[i] || '0', 10);
      let sum = digitA + digitB + carry;
      res = (sum % 10) + res;
      carry = Math.floor(sum / 10);
    }
    if (carry) res = carry + res;
    return res;
  }
  return addStrings(base, offset);
} 

/**
 * Получает url аватарки с кешированием в localStorage на 7 дней
 * @param {string} steamid
 * @param {function} fetcher - функция, возвращающая Promise<url>
 * @returns {Promise<string|null>}
 */
export async function getCachedAvatar(steamid, fetcher) {
  if (!steamid) return null;
  const key = 'avatar_' + steamid;
  const cacheRaw = localStorage.getItem(key);
  const now = Date.now();
  const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 дней
  if (cacheRaw) {
    try {
      const cache = JSON.parse(cacheRaw);
      if (cache.url && cache.ts && now - cache.ts < maxAge) {
        return cache.url;
      }
    } catch {}
  }
  try {
    const url = await fetcher();
    if (url) {
      localStorage.setItem(key, JSON.stringify({ url, ts: now }));
      return url;
    }
  } catch (error) {
    console.warn(`Failed to fetch avatar for ${steamid}:`, error.message);
  }
  return null;
} 

/**
 * Получает displayName с кешированием в localStorage на 24 часа
 * @param {string} steamid
 * @param {function} fetcher - функция, возвращающая Promise<displayName>
 * @returns {Promise<string|null>}
 */
export async function getCachedDisplayName(steamid, fetcher) {
  if (!steamid) return null;
  const key = 'displayName_' + steamid;
  const cacheRaw = localStorage.getItem(key);
  const now = Date.now();
  const maxAge = 24 * 60 * 60 * 1000; // 24 часа
  if (cacheRaw) {
    try {
      const cache = JSON.parse(cacheRaw);
      if (cache.value && cache.ts && now - cache.ts < maxAge) {
        return cache.value;
      }
    } catch {}
  }
  try {
    const value = await fetcher();
    if (value) {
      localStorage.setItem(key, JSON.stringify({ value, ts: now }));
      return value;
    }
  } catch (error) {
    console.warn(`Failed to fetch displayName for ${steamid}:`, error.message);
  }
  return null;
} 

/**
 * Обновляет displayName в кеше (localStorage) для steamid64
 * @param {string} steamid
 * @param {string} newName
 */
export function updateCachedDisplayName(steamid, newName) {
  if (!steamid || !newName) return;
  let id64 = steamid;
  if (!/^\d{17}$/.test(steamid)) {
    id64 = steamIdTo64(steamid);
  }
  if (!id64) return;
  const key = 'displayName_' + id64;
  localStorage.setItem(key, JSON.stringify({ value: newName, ts: Date.now() }));
}

export function proxyImageSrc(url) {
  if (!url || !/^https:\/\//i.test(url)) return url;
  return `/api/players/proxy-image?url=${encodeURIComponent(url)}`;
} 